import { useMemo, useEffect, useState, useRef, useCallback } from 'react'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { isAuthenticated, selectUserId } from '~/auth/slices/auth.slice'
import { learningStats } from '~/redux/apis/user-stats-api'
import { formatLearningStats } from '~/statistics/utils/format-stat.utils'
import { LearningStatsAPI } from '~/statistics/types/profile-card.types'

const tenMinutesInMs = 10 * 60 * 1000

export default function useExceededDailyTimeSpent(timeToExceedInMs = tenMinutesInMs): boolean {
  const authenticated = useAppSelector(isAuthenticated)
  const [hasExceeded, setHasExceeded] = useState(false)
  const [hasInitialData, setHasInitialData] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const midnightIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [data, setData] = useState<LearningStatsAPI | null>(null)
  const userId = useAppSelector(selectUserId)
  const dispatch = useAppDispatch()

  const timeSpan = useMemo<{ startDate: Date; endDate: Date }>(() => {
    const startDate = new Date()
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date()
    endDate.setHours(23, 59, 59, 999)
    return { startDate, endDate }
  }, [])

  const getDailyTimeSpent = useCallback(() => {
    if (!authenticated) return
    setData(null)
    setHasInitialData(false)

    const apiCall = dispatch(
      learningStats.initiate({
        startDate: timeSpan.startDate.toISOString(),
        endDate: timeSpan.endDate.toISOString(),
      }),
    )

    apiCall
      .unwrap()
      .then((freshData) => {
        setData(freshData)
        setHasInitialData(true)
      })
      .catch((error) => {
        console.error('❌ Midnight API call failed:', error)
      })

    return () => {
      apiCall.unsubscribe()
    }
  }, [authenticated, dispatch, timeSpan])

  // Schedule midnight API calls
  useEffect(() => {
    if (!authenticated) return

    const getTimeUntilMidnight = () => {
      const now = new Date()
      const midnight = new Date()
      midnight.setHours(24, 0, 0, 5) // Next midnight + 5ms
      return midnight.getTime() - now.getTime()
    }

    const timeUntilMidnight = getTimeUntilMidnight()

    const firstTimeout = setTimeout(() => {
      getDailyTimeSpent()

      midnightIntervalRef.current = setInterval(() => {
        getDailyTimeSpent()
      }, 24 * 60 * 60 * 1000) // 24 hours
    }, timeUntilMidnight)

    return () => {
      clearTimeout(firstTimeout)
      if (midnightIntervalRef.current) {
        clearInterval(midnightIntervalRef.current)
        midnightIntervalRef.current = null
      }
    }
  }, [authenticated, getDailyTimeSpent])

  // Manual API call when needed (initial load)
  useEffect(() => {
    if (!authenticated || hasInitialData) return
    getDailyTimeSpent()
  }, [authenticated, hasInitialData, getDailyTimeSpent])

  // Process data when it arrives
  useEffect(() => {
    if (!authenticated || !data) return

    const { totalTimeSpent } = formatLearningStats(data)

    if ((totalTimeSpent || 0) >= timeToExceedInMs) {
      setHasExceeded(true)
    } else {
      setHasExceeded(false)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      const remainingTime = timeToExceedInMs - (totalTimeSpent || 0)
      timeoutRef.current = setTimeout(() => {
        setHasExceeded(true)
      }, remainingTime)
    }
  }, [authenticated, data, timeToExceedInMs])

  // Reset when user changes
  useEffect(() => {
    cleanUp()
  }, [userId])

  // Reset state when authentication changes
  useEffect(() => {
    if (!authenticated) {
      cleanUp()
    }
    return () => {
      cleanUp()
    }
  }, [authenticated])

  const cleanUp = (): void => {
    setHasExceeded(false)
    setHasInitialData(false)
    setData(null)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (midnightIntervalRef.current) {
      clearInterval(midnightIntervalRef.current)
      midnightIntervalRef.current = null
    }
  }

  return hasExceeded
}
